import { create } from 'zustand';

interface DeepLinkStore {
  isCheckingApp: boolean;
  appInstalled: boolean | null;
  deepLinkUrl: string | null;
  setCheckingApp: (checking: boolean) => void;
  setAppInstalled: (installed: boolean | null) => void;
  setDeepLinkUrl: (url: string | null) => void;
  checkAppAvailability: (deepLink: string) => Promise<boolean>;
}

/**
 * Store for managing deep link app detection state
 * Prevents error popups by checking app availability before redirecting
 */
export const useDeepLinkStore = create<DeepLinkStore>((set, _get) => ({
  isCheckingApp: false,
  appInstalled: null,
  deepLinkUrl: null,

  setCheckingApp: (checking) => set({ isCheckingApp: checking }),
  
  setAppInstalled: (installed) => set({ appInstalled: installed }),
  
  setDeepLinkUrl: (url) => set({ deepLinkUrl: url }),

  /**
   * Checks if the app is installed by attempting to open a deep link
   * and checking if the page loses focus (indicating app opened)
   */
  checkAppAvailability: async (deepLink: string): Promise<boolean> => {
    set({ isCheckingApp: true, deepLinkUrl: deepLink });

    return new Promise((resolve) => {
      let resolved = false;
      const timeout = setTimeout(() => {
        if (!resolved) {
          resolved = true;
          set({ isCheckingApp: false, appInstalled: false });
          resolve(false); // Assume not installed if timeout
        }
      }, 2000); // 2 second timeout

      // Track if page loses focus (app opened)
      const handleBlur = () => {
        if (!resolved) {
          resolved = true;
          clearTimeout(timeout);
          set({ isCheckingApp: false, appInstalled: true });
          window.removeEventListener('blur', handleBlur);
          resolve(true);
        }
      };

      // Track if page visibility changes (app opened)
      const handleVisibilityChange = () => {
        if (document.hidden && !resolved) {
          resolved = true;
          clearTimeout(timeout);
          set({ isCheckingApp: false, appInstalled: true });
          document.removeEventListener('visibilitychange', handleVisibilityChange);
          resolve(true);
        }
      };

      window.addEventListener('blur', handleBlur);
      document.addEventListener('visibilitychange', handleVisibilityChange);

      // Try to open the deep link
      try {
        window.location.href = deepLink;
      } catch (e) {
        // If we can't even try to open it, assume not installed
        if (!resolved) {
          resolved = true;
          clearTimeout(timeout);
          set({ isCheckingApp: false, appInstalled: false });
          window.removeEventListener('blur', handleBlur);
          document.removeEventListener('visibilitychange', handleVisibilityChange);
          resolve(false);
        }
      }

      // Fallback: if still not resolved after timeout, check if we're still focused
      setTimeout(() => {
        if (!resolved) {
          resolved = true;
          clearTimeout(timeout);
          // If page is still focused/visible, app likely didn't open
          const installed = !document.hidden && document.hasFocus();
          set({ isCheckingApp: false, appInstalled: installed });
          window.removeEventListener('blur', handleBlur);
          document.removeEventListener('visibilitychange', handleVisibilityChange);
          resolve(installed);
        }
      }, 1500);
    });
  },
}));
